import { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  } catch { return null; }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function useNotifications() {
  const [permission, setPermission] = useState(() => 
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState(() => 
    localStorage.getItem('perkly_push_subscribed') === 'true'
  );

  const subscribe = async () => {
    const reg = await registerServiceWorker();
    if (!reg) return false;

    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm !== 'granted') return false;

    try {
      // Get VAPID public key
      const keyRes = await fetch(`${API}/notifications/vapid-public-key`);
      const { public_key } = await keyRes.json();
      const appServerKey = urlBase64ToUint8Array(public_key);

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey,
      });

      // Send subscription to backend
      const subJson = sub.toJSON();
      await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          city_id: 'athens_ga',
        }),
      });

      localStorage.setItem('perkly_push_subscribed', 'true');
      setSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push subscribe error:', err);
      return false;
    }
  };

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await fetch(`${API}/notifications/unsubscribe`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ endpoint: sub.endpoint }),
          });
          await sub.unsubscribe();
        }
      }
    } catch {}
    localStorage.removeItem('perkly_push_subscribed');
    setSubscribed(false);
  };

  return { permission, subscribed, subscribe, unsubscribe };
}

/* Compact bell button for Navbar */
export function NotificationBell() {
  const { permission, subscribed, subscribe, unsubscribe } = useNotifications();
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!('Notification' in window) || !('serviceWorker' in navigator)) return null;

  const handleToggle = async () => {
    setLoading(true);
    if (subscribed) {
      await unsubscribe();
      setShowToast('Notifications disabled');
    } else {
      const ok = await subscribe();
      setShowToast(ok ? 'Daily deal alerts enabled!' : 'Permission denied');
    }
    setLoading(false);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <div className="relative">
      <button
        onClick={handleToggle}
        disabled={loading}
        data-testid="notification-bell-btn"
        title={subscribed ? 'Disable deal alerts' : 'Enable daily deal alerts'}
        className={`relative flex items-center justify-center w-9 h-9 rounded-full border transition-all ${
          subscribed
            ? 'bg-[#C8A97E]/15 border-[#C8A97E]/40 text-[#C8A97E]'
            : 'border-[#8A9A8B]/30 text-[#2B2B2B]/50 hover:border-[#C8A97E] hover:text-[#C8A97E]'
        }`}
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : subscribed ? (
          <Bell size={16} fill="currentColor" />
        ) : (
          <Bell size={16} />
        )}
        {subscribed && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#C8A97E] rounded-full border-2 border-white" />
        )}
      </button>

      {showToast && (
        <div className="absolute top-12 right-0 bg-[#2B2B2B] text-white text-xs font-semibold rounded-xl px-3 py-2 whitespace-nowrap shadow-lg z-50">
          {showToast}
        </div>
      )}
    </div>
  );
}

/* Full-page notification opt-in banner */
export function NotificationBanner() {
  const { permission, subscribed, subscribe } = useNotifications();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('perkly_notif_dismissed') === 'true');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  if (dismissed || subscribed || permission === 'denied') return null;

  const handleEnable = async () => {
    setLoading(true);
    const ok = await subscribe();
    if (ok) setSuccess(true);
    else setDismissed(true);
    setLoading(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('perkly_notif_dismissed', 'true');
    setDismissed(true);
  };

  if (success) return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#2B2B2B] text-white rounded-2xl px-6 py-4 shadow-xl flex items-center gap-3 animate-fade-in-up">
      <Check size={18} className="text-[#C8A97E]" />
      <span className="text-sm font-semibold">Daily deal alerts enabled!</span>
    </div>
  );

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-0 sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 sm:w-auto"
      data-testid="notification-banner"
    >
      <div className="bg-[#2B2B2B] rounded-2xl sm:rounded-2xl p-5 shadow-2xl border border-white/10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:max-w-lg">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-[#C8A97E]/20 flex items-center justify-center flex-shrink-0">
            <Bell size={18} className="text-[#C8A97E]" />
          </div>
          <div>
            <p className="font-bold text-white text-sm">Get Daily Deal Alerts</p>
            <p className="text-white/55 text-xs mt-0.5">Be first to know about food deals, freebies & events near you — every morning.</p>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={handleEnable}
            disabled={loading}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-[#C8A97E] text-white text-sm font-bold hover:bg-[#b8956b] transition-colors disabled:opacity-60"
            data-testid="enable-notifications-btn"
          >
            {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Bell size={13} /> Enable</>}
          </button>
          <button onClick={handleDismiss} className="p-2 text-white/40 hover:text-white/70 transition-colors" data-testid="dismiss-notifications-btn">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
